import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Warning, CheckCircle, FunnelSimple, ArrowRight } from "@phosphor-icons/react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";

export default function Alerts({ programme = "CI" }) {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ route_id: "all", status: "all", cabin_class: "all", alert_type: "all" });
  const isPX = programme === "PX";
  const home = isPX ? "PX" : "CI";

  useEffect(() => {
    Promise.all([
      api.get("/alerts", { params: {
        programme,
        route_id: filters.route_id !== "all" ? filters.route_id : undefined,
        status: filters.status !== "all" ? filters.status : undefined,
        cabin_class: filters.cabin_class !== "all" ? filters.cabin_class : undefined,
        alert_type: filters.alert_type !== "all" ? filters.alert_type : undefined,
      } }),
      api.get("/routes", { params: { programme } })
    ]).then(([alertRes, routeRes]) => {
      setAlerts(alertRes.data);
      setRoutes(routeRes.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [api, filters, programme]);

  const handleResolve = async (alertId) => {
    await api.put(`/alerts/${alertId}/resolve`);
    setAlerts(prev => prev.map(a => a.id === alertId ? { ...a, status: "resolved" } : a));
  };

  if (loading) {
    return <div className="space-y-4">{[1,2,3,4].map(i => <div key={i} className="skeleton h-20 rounded-xl" />)}</div>;
  }

  return (
    <div data-testid="alerts-page" className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-syne font-bold text-2xl text-white tracking-tight">{isPX ? "Air Niugini Alerts" : "Alerts Centre"}</h1>
          <p className="text-text-secondary text-sm mt-1">{alerts.length} alert{alerts.length !== 1 ? 's' : ''} found</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 bg-card-bg border border-[rgba(255,255,255,0.08)] rounded-xl p-4">
        <FunnelSimple size={18} className="text-text-secondary" />
        <Select value={filters.route_id} onValueChange={(v) => setFilters(f => ({ ...f, route_id: v }))}>
          <SelectTrigger data-testid="filter-route" className="w-40 bg-navy border-[rgba(255,255,255,0.1)] text-white text-sm h-9">
            <SelectValue placeholder="All Routes" />
          </SelectTrigger>
          <SelectContent className="bg-card-bg border-[rgba(255,255,255,0.1)]">
            <SelectItem value="all">All Routes</SelectItem>
            {routes.map(r => <SelectItem key={r.id} value={r.id}>AKL-{r.destination_code}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.status} onValueChange={(v) => setFilters(f => ({ ...f, status: v }))}>
          <SelectTrigger data-testid="filter-status" className="w-36 bg-navy border-[rgba(255,255,255,0.1)] text-white text-sm h-9">
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent className="bg-card-bg border-[rgba(255,255,255,0.1)]">
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="resolved">Resolved</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filters.cabin_class} onValueChange={(v) => setFilters(f => ({ ...f, cabin_class: v }))}>
          <SelectTrigger data-testid="filter-cabin" className="w-44 bg-navy border-[rgba(255,255,255,0.1)] text-white text-sm h-9">
            <SelectValue placeholder="All Classes" />
          </SelectTrigger>
          <SelectContent className="bg-card-bg border-[rgba(255,255,255,0.1)]">
            <SelectItem value="all">All Classes</SelectItem>
            <SelectItem value="economy">Economy</SelectItem>
            {!isPX && <SelectItem value="premium_economy">Premium Economy</SelectItem>}
            <SelectItem value="business">Business</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filters.alert_type} onValueChange={(v) => setFilters(f => ({ ...f, alert_type: v }))}>
          <SelectTrigger data-testid="filter-alert-type" className="w-48 bg-navy border-[rgba(255,255,255,0.1)] text-white text-sm h-9">
            <SelectValue placeholder="All Types" />
          </SelectTrigger>
          <SelectContent className="bg-card-bg border-[rgba(255,255,255,0.1)]">
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="gap">Fare gap</SelectItem>
            <SelectItem value="competitor_movement">Competitor movement</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Alert list */}
      {alerts.length === 0 ? (
        <div className="bg-card-bg border border-[rgba(255,255,255,0.08)] rounded-xl p-12 text-center">
          <CheckCircle size={48} className="text-success mx-auto mb-4" weight="duotone" />
          <h3 className="font-syne font-bold text-white text-lg mb-2">No Alerts</h3>
          <p className="text-text-secondary text-sm">{isPX ? "No fare gaps or competitor movements detected for Air Niugini routes." : "CI fares are competitive across all routes. Great news!"}</p>
        </div>
      ) : (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-3">
          {alerts.map((alert, i) => (
            <motion.div key={alert.id || i}
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
              data-testid={`alert-item-${i}`}
              className={`bg-card-bg border rounded-xl p-4 flex items-center gap-4 ${
                alert.severity === 'critical' ? 'border-danger/30' : 'border-warning/30'
              }`}>
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
                alert.severity === 'critical' ? 'bg-danger/15' : 'bg-warning/15'
              }`}>
                <Warning size={20} weight="fill" className={alert.severity === 'critical' ? 'text-danger' : 'text-warning'} />
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className="text-white font-medium text-sm">AKL {"\u2192"} {alert.route_destination}</span>
                  <Badge variant="outline" className={`text-xs ${alert.severity === 'critical' ? 'border-danger/40 text-danger' : 'border-warning/40 text-warning'}`}>
                    {alert.severity}
                  </Badge>
                  <Badge variant="outline" className={`text-xs ${alert.status === 'active' ? 'border-ci-orange/40 text-ci-orange' : 'border-success/40 text-success'}`}>
                    {alert.status}
                  </Badge>
                </div>
                {alert.alert_type === "competitor_movement" ? (
                  <>
                    <p className="text-text-secondary text-xs" data-testid={`alert-movement-${i}`}>
                      <span className="font-mono text-white">{alert.competitor_name} ({alert.competitor_iata})</span>
                      {" "}fare moved <span className={`font-mono ${alert.direction === "down" ? "text-danger" : "text-success"}`}>{alert.delta_percent > 0 ? "+" : ""}{alert.delta_percent}%</span> in{" "}
                      <span className="capitalize">{alert.cabin_class?.replace('_', ' ')}</span>
                      {" "}&mdash; {new Date(0, (alert.travel_month || 1) - 1).toLocaleString('en', {month: 'long'})} {alert.travel_year}
                    </p>
                    <div className="flex items-center gap-4 mt-1 text-xs font-mono">
                      <span className="text-text-secondary">Was: <span className="text-white">NZ${alert.old_fare_nzd?.toLocaleString()}</span></span>
                      <span className="text-text-secondary">Now: <span className={alert.direction === "down" ? "text-danger" : "text-success"}>NZ${alert.new_fare_nzd?.toLocaleString()}</span></span>
                      <span className="text-text-secondary">Δ NZ${alert.delta_nzd?.toLocaleString()}</span>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="text-text-secondary text-xs">
                      <span className="font-mono text-white">{alert.competitor_name} ({alert.competitor_iata})</span>
                      {" "}is <span className="font-mono text-danger">{alert.gap_percent}%</span> cheaper in{" "}
                      <span className="capitalize">{alert.cabin_class?.replace('_', ' ')}</span>
                      {" "}&mdash; {new Date(0, (alert.travel_month || 1) - 1).toLocaleString('en', {month: 'long'})} {alert.travel_year}
                    </p>
                    <div className="flex items-center gap-4 mt-1 text-xs font-mono">
                      <span className="text-text-secondary">{home}: <span className="text-white">NZ${alert.ci_fare_nzd?.toLocaleString()}</span></span>
                      <span className="text-text-secondary">{alert.competitor_iata}: <span className="text-danger">NZ${alert.competitor_fare_nzd?.toLocaleString()}</span></span>
                      <span className="text-danger">Gap: NZ${alert.gap_nzd?.toLocaleString()}</span>
                    </div>
                  </>
                )}
              </div>

              <div className="flex items-center gap-2 shrink-0">
                {alert.status === "active" && (
                  <button onClick={() => handleResolve(alert.id)} data-testid={`resolve-alert-${i}`}
                    className="text-xs text-text-secondary hover:text-success transition-colors px-3 py-1.5 rounded-lg border border-[rgba(255,255,255,0.08)] hover:border-success/30">
                    Resolve
                  </button>
                )}
                <button onClick={() => navigate(`${isPX ? "/px" : ""}/routes/${alert.route_id}`)}
                  className="text-xs text-ci-orange hover:underline flex items-center gap-1">
                  View <ArrowRight size={12} />
                </button>
              </div>
            </motion.div>
          ))}
        </motion.div>
      )}
    </div>
  );
}
